import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { errorMessageKey } from '../api/api-error';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent } from '../components/ui/dialog';
import { Notice } from '../components/ui/notice';
import { TextField } from '../components/ui/text-field';
import { diagramFormSchema, type DiagramFormValues } from '../diagrams/diagram-schema';
import { useI18n } from '../i18n/i18n';

interface DiagramFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  submitLabel: string;
  initial: DiagramFormValues;
  onSubmit: (values: { name: string; description: string | null }) => Promise<void>;
}

/** Имя и описание схемы: «+» на домашнем экране, «Переименовать», «Дублировать». */
export function DiagramFormDialog({
  open,
  onOpenChange,
  title,
  submitLabel,
  initial,
  onSubmit,
}: DiagramFormDialogProps) {
  const { t } = useI18n();
  const [error, setError] = useState<unknown>(null);
  const form = useForm<DiagramFormValues>({
    resolver: zodResolver(diagramFormSchema),
    values: initial,
  });
  const submit = form.handleSubmit(async (values) => {
    setError(null);
    try {
      await onSubmit({
        name: values.name.trim(),
        description: values.description.trim() === '' ? null : values.description,
      });
      onOpenChange(false);
    } catch (caught) {
      setError(caught);
    }
  });
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (next) setError(null);
        onOpenChange(next);
      }}
    >
      <DialogContent
        title={title}
        footer={
          <>
            <Button type="submit" form="diagram-form" disabled={form.formState.isSubmitting}>
              {submitLabel}
            </Button>
            <Button variant="secondary" onClick={() => onOpenChange(false)}>
              {t('common.cancel')}
            </Button>
          </>
        }
      >
        <form id="diagram-form" onSubmit={submit} className="space-y-4">
          <TextField control={form.control} name="name" label={t('diagram.name')} autoFocus />
          <TextField
            control={form.control}
            name="description"
            label={t('card.description')}
            multiline
          />
          {error !== null && <Notice tone="danger">{t(errorMessageKey(error))}</Notice>}
        </form>
      </DialogContent>
    </Dialog>
  );
}
